import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../Shared/Formatters';
import DeltaBadge from '../Shared/DeltaBadge';

/**
 * TenantKpiStrip — Sprint 3.9
 *
 * Replaces the 10 period-widget grid from the legacy WidgetsWraper.jsx.
 * Backend returns string-typed numbers (".toFixed(2)") so we coerce before format.
 *
 * Renders two groups (Sales + Revenue) × 5 periods plus a "Custom range" pair
 * driven by the gettotals endpoint when the user picks a date range.
 */

function toNumber(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
}

function Card({ label, value, accent = false }) {
    return (
        <div className={`v3-td-kpi-card${accent ? ' accent' : ''}`}>
            <span className="lbl">{label}</span>
            <span className="val">{formatMoney(value)}</span>
        </div>
    );
}

function SkeletonCard() {
    return (
        <div className="v3-td-kpi-card">
            <span className="lbl">&nbsp;</span>
            <span className="val v3-td-skeleton" style={{ height: 22, width: '70%' }}>&nbsp;</span>
        </div>
    );
}

export default function TenantKpiStrip({
    periodTotals, periodLoading, periodError, onRetryPeriod,
    customTotals, customLoading, customError, onRetryCustom, customRange,
}) {
    const { t } = useTranslation();

    return (
        <div className="v3-td-card v3-td-section">
            <h3 className="v3-td-card-title">{t('Performance Snapshot')}</h3>

            {periodError && (
                <div className="v3-td-error" style={{ marginBottom: 12 }}>
                    {t('Unable to load period totals')} — {periodError}
                    <button type="button" onClick={onRetryPeriod}>{t('Retry')}</button>
                </div>
            )}

            {/* Sales */}
            <div className="v3-td-kpi-group">
                <h4>
                    {t('Sales')}
                    {periodTotals?.growthPercentageSales !== undefined && (
                        <span style={{ marginLeft: 10 }}>
                            <DeltaBadge
                                value={toNumber(periodTotals.growthPercentageSales)}
                                suffix={t('% YoY')}
                            />
                        </span>
                    )}
                </h4>
                <div className="v3-td-kpi-grid">
                    {periodLoading || !periodTotals ? (
                        Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
                    ) : (
                        <>
                            <Card label={t('Yesterday')} value={toNumber(periodTotals.salesYesterday)} />
                            <Card label={t('This Week')} value={toNumber(periodTotals.salesThisWeek)} />
                            <Card label={t('This Month')} value={toNumber(periodTotals.salesThisMonth)} />
                            <Card label={t('YTD')} value={toNumber(periodTotals.salesThisYear)} accent />
                            <Card label={t('LY YTD')} value={toNumber(periodTotals.salesPreviousYear)} />
                        </>
                    )}
                </div>
            </div>

            {/* Revenue */}
            <div className="v3-td-kpi-group">
                <h4>
                    {t('Revenue')}
                    {periodTotals?.growthPercentageRevenue !== undefined && (
                        <span style={{ marginLeft: 10 }}>
                            <DeltaBadge
                                value={toNumber(periodTotals.growthPercentageRevenue)}
                                suffix={t('% YoY')}
                            />
                        </span>
                    )}
                </h4>
                <div className="v3-td-kpi-grid">
                    {periodLoading || !periodTotals ? (
                        Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
                    ) : (
                        <>
                            <Card label={t('Yesterday')} value={toNumber(periodTotals.revenueYesterday)} />
                            <Card label={t('This Week')} value={toNumber(periodTotals.revenueThisWeek)} />
                            <Card label={t('This Month')} value={toNumber(periodTotals.revenueThisMonth)} />
                            <Card label={t('YTD')} value={toNumber(periodTotals.revenueThisYear)} accent />
                            <Card label={t('LY YTD')} value={toNumber(periodTotals.revenuePreviousYear)} />
                        </>
                    )}
                </div>
            </div>

            {/* Custom range — only render if user picked a range */}
            {customRange?.from && customRange?.to && (
                <div className="v3-td-kpi-group">
                    <h4>{t('Custom Range')} ({customRange.from} — {customRange.to})</h4>
                    {customError && (
                        <div className="v3-td-error" style={{ marginBottom: 8 }}>
                            {customError}
                            <button type="button" onClick={onRetryCustom}>{t('Retry')}</button>
                        </div>
                    )}
                    <div className="v3-td-kpi-grid">
                        {customLoading || !customTotals ? (
                            Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
                        ) : (
                            <>
                                <Card label={t('Total Sales')} value={toNumber(customTotals.totalSales)} accent />
                                <Card label={t('Total Revenue')} value={toNumber(customTotals.totalRevenue)} accent />
                                <div className="v3-td-kpi-card">
                                    <span className="lbl">{t('Live Days')}</span>
                                    <span className="val">{customTotals.live_days ?? '—'}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
